'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { initMixpanel, trackPageView } from '@/lib/mixpanel';

export default function MixpanelProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const initialized = useRef(false);

  // Defer Mixpanel init until the browser is idle (after first paint + interaction)
  // This prevents mixpanel-browser from blocking the main thread during initial load.
  useEffect(() => {
    if (initialized.current) return;

    const init = () => {
      if (initialized.current) return;
      initialized.current = true;
      initMixpanel();
    };

    // Use requestIdleCallback when available (Chrome/Edge/Firefox), fall back to setTimeout
    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: object) => void })
          .requestIdleCallback(init, { timeout: 3000 });
      } else {
        // Fallback: defer 2 seconds after mount so it doesn't block LCP
        const timer = setTimeout(init, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  // Track page views only after Mixpanel has been initialized
  useEffect(() => {
    if (!pathname) return;

    const pageNameMap: Record<string, string> = {
      '/': 'Home',
      '/sign-up-login-screen': 'Sign Up / Login',
      '/dashboard': 'Dashboard',
      '/create-production': 'Create Production',
      '/onboarding-tracker': 'Onboarding Tracker',
      '/contract-management': 'Contract Management',
      '/contractor-roster': 'Contractor Roster',
      '/schedule-management': 'Schedule Management',
      '/offer-workflow': 'Offer Workflow',
      '/tax-portal': 'Tax Portal',
      '/communications-hub': 'Communications Hub',
      '/external-partners': 'External Partners',
      '/payments-dashboard': 'Payments Dashboard',
      '/analytics': 'Analytics',
      '/budget': 'Budget',
      '/compliance': 'Compliance',
      '/payment-reminders': 'Payment Reminders',
      '/client-invoicing': 'Client Invoicing',
      '/financial-export': 'Financial Export',
      '/workflow-automation': 'Workflow Automation',
      '/message-templates': 'Message Templates',
      '/notifications': 'Notifications Center',
      '/contractor-earnings': 'Contractor Earnings',
      '/admin/analytics': 'Admin Analytics',
      '/admin/audit-logs': 'Compliance Audit Logs',
      '/admin/user-management': 'User Management',
      '/admin/disbursements': 'Pending Disbursements',
      '/admin/payment-history': 'Payout History',
    };

    const pageName = pageNameMap[pathname] || pathname;

    // Defer page view tracking slightly to ensure Mixpanel is ready
    const timer = setTimeout(() => {
      trackPageView(pageName, { path: pathname });
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname]);

  return <>{children}</>;
}
